import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Trash2, MessageCircle, Users, Music } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuthStore } from '../store/authStore';
import { useNavigate, useSearchParams } from 'react-router-dom';

const CommunityChat = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'overseer';
  const room = 'artist'; // Only artist chat room now

  const fetchMessages = useCallback(async () => {
    try {
      const response = await api.get(`/community-chat?limit=100&room=${encodeURIComponent(room)}`);
      setMessages(response.data);
      setHasAccess(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setHasAccess(false);
      } else {
        console.error('Failed to fetch messages:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [room]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMessages();
      // Poll for new messages every 5 seconds
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, fetchMessages]);

  useEffect(() => {
    // Scroll the message CONTAINER (not the whole page)
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const response = await api.post('/community-chat', { content: newMessage, room });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      await api.delete(`/community-chat/${messageId}`);
      setMessages(prev => prev.filter(m => m.message_id !== messageId));
      toast.success('Message deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'overseer':
        return (
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500/20 via-green-500/20 to-teal-500/20 text-emerald-200 border border-emerald-400/30 shadow-[0_0_15px_rgba(16,185,129,0.4)]">
            {/* Overseer - Green Spinning Planet with Blinking Eye */}
            <span className="relative w-5 h-5 flex items-center justify-center animate-spin" style={{ animationDuration: '4s' }}>
              {/* Glow effect */}
              <span className="absolute inset-[-3px] rounded-full bg-gradient-to-r from-emerald-400/60 via-green-400/60 to-teal-400/60 blur-sm animate-pulse" />
              
              {/* Planet body */}
              <span className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-300 via-green-400 to-teal-500 shadow-[0_0_10px_rgba(16,185,129,0.8),0_0_20px_rgba(20,184,166,0.5)]">
                {/* Blinking Eye */}
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-1.5 bg-white/90 rounded-full flex items-center justify-center animate-pulse" style={{ animationDuration: '1.5s' }}>
                  <span className="w-1 h-1 bg-emerald-900 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
                </span>
              </span>
            </span>
            <span className="font-bold bg-gradient-to-r from-emerald-300 via-green-300 to-teal-300 bg-clip-text text-transparent">Overseer</span>
          </span>
        );
      case 'owner':
        return (
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-orange-500/20 via-amber-500/20 to-yellow-500/20 text-orange-200 border border-orange-400/30 shadow-[0_0_15px_rgba(251,146,60,0.4)]">
            {/* Owner - Orange Spinning Glowing Planet */}
            <span className="relative w-4 h-4 flex items-center justify-center animate-spin" style={{ animationDuration: '3s' }}>
              {/* Glow effect */}
              <span className="absolute inset-[-3px] rounded-full bg-gradient-to-r from-orange-400/60 via-amber-400/60 to-yellow-400/60 blur-sm animate-pulse" />
              
              {/* Planet body - spinning circle */}
              <span className="absolute inset-0 rounded-full bg-gradient-to-br from-orange-300 via-amber-400 to-yellow-500 shadow-[0_0_10px_rgba(251,146,60,0.8),0_0_20px_rgba(245,158,11,0.5)]">
                {/* Surface details */}
                <span className="absolute top-0.5 left-1 w-1 h-1 rounded-full bg-white/60" />
                <span className="absolute bottom-1 right-0.5 w-0.5 h-0.5 rounded-full bg-white/50" />
              </span>
            </span>
            <span className="font-bold bg-gradient-to-r from-orange-300 via-amber-300 to-yellow-300 bg-clip-text text-transparent">Owner</span>
          </span>
        );
      case 'admin':
        return (
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 text-cyan-200 border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.3)]">
            {/* Spinning Glowing Planet - Circle */}
            <span className="relative w-4 h-4 flex items-center justify-center animate-spin" style={{ animationDuration: '4s' }}>
              {/* Glow effect */}
              <span className="absolute inset-[-2px] rounded-full bg-gradient-to-r from-cyan-400/50 via-purple-400/50 to-pink-400/50 blur-sm" />
              
              {/* Planet body - spinning circle */}
              <span className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-300 via-purple-400 to-pink-500 shadow-[0_0_8px_rgba(34,211,238,0.6),0_0_16px_rgba(168,85,247,0.4)]">
                {/* Surface details */}
                <span className="absolute top-0.5 left-1 w-1 h-1 rounded-full bg-white/50" />
                <span className="absolute bottom-1 right-0.5 w-0.5 h-0.5 rounded-full bg-white/40" />
              </span>
            </span>
            <span className="font-semibold bg-gradient-to-r from-cyan-300 via-purple-300 to-pink-300 bg-clip-text text-transparent">Admin</span>
          </span>
        );
      case 'artist':
        return (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-900/30 text-rose-200 border border-rose-500/20">
            <Music className="w-3 h-3" />
            Artist
          </span>
        );
      default:
        return null;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-purple-500/20 flex items-center justify-center">
            <MessageCircle className="w-10 h-10 text-purple-400" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Artist Chat</h1>
          <p className="text-white/60 mb-8">
            Sign in to join the artist chat (admins included).
          </p>
          <Button
            onClick={() => {
              window.location.href = `/login?redirect=${encodeURIComponent('/community-chat?room=artist')}`;
            }}
            className="bg-gradient-to-r from-purple-600 to-indigo-600"
          >
            Sign In to Continue
          </Button>
        </div>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-white/60">Loading chat...</p>
        </div>
      </div>
    );
  }

  // Room access control - only artists and admins can access
  if (!isAdmin && user?.role !== 'artist') {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <Users className="w-10 h-10 text-yellow-400" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Artist Chat</h1>
          <p className="text-white/60 mb-8">This chat room is for artists (and admins) only.</p>
          <Button onClick={() => navigate('/create-artist-profile')} className="bg-gradient-to-r from-purple-600 to-indigo-600">
            <Music className="w-4 h-4 mr-2" />
            Become an Artist
          </Button>
        </div>
      </div>
    );
  }

  // No access (fallback)
  if (!hasAccess) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <Users className="w-10 h-10 text-yellow-400" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Artist Chat</h1>
          <p className="text-white/60 mb-8">
            This chat room is for artists (and admins) only.
          </p>
          <Button
            onClick={() => navigate('/create-artist-profile')}
            className="bg-gradient-to-r from-purple-600 to-indigo-600"
          >
            <Music className="w-4 h-4 mr-2" />
            Become an Artist
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 relative z-10">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-500/20 flex items-center justify-center">
            <MessageCircle className="w-8 h-8 text-purple-400" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Artist Chat</h1>
          <p className="text-white/60">
            Artists only (admins included)
          </p>
        </div>

        {/* Chat Container */}
        <div className="glass-default rounded-xl border border-white/10 overflow-hidden">
          {/* Messages Area */}
          <div 
            ref={chatContainerRef}
            className="h-[500px] overflow-y-auto p-4 space-y-4"
          >
            {messages.length === 0 ? (
              <div className="text-center py-12 text-white/40">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages.map((message) => {
                const isOwnMessage = message.user_id === user?.user_id;
                const isAdminMessage = message.user_role === 'admin';
                const isOwnerMessage = message.user_role === 'owner';
                const isOverseerMessage = message.user_role === 'overseer';
                const isAdminOrOwner = isAdminMessage || isOwnerMessage || isOverseerMessage;
                return (
                  <div
                    key={message.message_id}
                    className={`flex gap-3 ${isOwnMessage ? 'flex-row-reverse' : ''}`}
                  >
                    {/* Profile Image */}
                    <div className="flex-shrink-0 relative">
                      {isOverseerMessage && (
                        /* Green glow around overseer profile */
                        <span className="absolute -inset-1 rounded-full bg-emerald-400/60 blur-md" style={{ aspectRatio: '1/1' }} />
                      )}
                      {isOwnerMessage && (
                        /* Orange glow around owner profile */
                        <span className="absolute -inset-1 rounded-full bg-orange-400/60 blur-md" style={{ aspectRatio: '1/1' }} />
                      )}
                      {isAdminMessage && (
                        /* Cyan glow around admin profile */
                        <span className="absolute -inset-1 rounded-full bg-cyan-400/50 blur-md" style={{ aspectRatio: '1/1' }} />
                      )}
                      {message.profile_image ? (
                        <img
                          src={message.profile_image}
                          alt={message.user_name}
                          className={`w-10 h-10 rounded-full object-cover relative z-10 ${
                            isOverseerMessage ? 'ring-2 ring-emerald-400/70' :
                            isOwnerMessage ? 'ring-2 ring-orange-400/70' : 
                            isAdminMessage ? 'ring-2 ring-cyan-400/70' : ''
                          }`}
                        />
                      ) : (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold relative z-10 ${
                          isOverseerMessage
                            ? 'bg-gradient-to-br from-emerald-400 via-green-500 to-teal-500 ring-2 ring-emerald-400/70'
                            : isOwnerMessage 
                              ? 'bg-gradient-to-br from-orange-400 via-amber-500 to-yellow-500 ring-2 ring-orange-400/70'
                              : isAdminMessage 
                                ? 'bg-gradient-to-br from-cyan-400 via-purple-500 to-pink-500 ring-2 ring-cyan-400/70' 
                                : 'bg-gradient-to-br from-purple-500 to-indigo-600'
                        }`}>
                          {message.user_name?.charAt(0).toUpperCase() || '?'}
                        </div>
                      )}
                    </div>

                    {/* Message Content */}
                    <div className={`flex-1 max-w-[70%] ${isOwnMessage ? 'text-right' : ''}`}>
                      {/* User Info */}
                      <div className={`flex items-center gap-2 mb-1 ${isOwnMessage ? 'justify-end' : ''}`}>
                        <span className="font-semibold text-white text-sm">
                          {message.artist_name || message.user_name}
                        </span>
                        {message.username && (
                          <span className="text-xs text-white/40">@{message.username}</span>
                        )}
                        {getRoleBadge(message.user_role)}
                        <span className="text-xs text-white/40">
                          {formatTime(message.created_at)}
                        </span>
                      </div>

                      {/* Secondary name if artist */}
                      {message.artist_name && message.user_name && (
                        <p className={`text-xs text-white/40 mb-1 ${isOwnMessage ? 'text-right' : ''}`}>
                          {message.user_name}
                        </p>
                      )}

                      {/* Message Bubble */}
                      <div className={`relative group inline-block`}>
                        <div
                          className={`px-4 py-2 rounded-2xl ${
                            message.user_role === 'overseer'
                              ? 'bg-gradient-to-r from-emerald-500/20 via-green-500/20 to-teal-500/20 border border-emerald-400/30 text-white rounded-tr-sm shadow-[0_0_15px_rgba(16,185,129,0.3),0_0_30px_rgba(20,184,166,0.2)]'
                              : message.user_role === 'owner'
                                ? 'bg-gradient-to-r from-orange-500/20 via-amber-500/20 to-yellow-500/20 border border-orange-400/30 text-white rounded-tr-sm shadow-[0_0_15px_rgba(251,146,60,0.3),0_0_30px_rgba(245,158,11,0.2)]'
                                : message.user_role === 'admin'
                                  ? 'bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 border border-cyan-400/30 text-white rounded-tr-sm shadow-[0_0_15px_rgba(34,211,238,0.2),0_0_30px_rgba(168,85,247,0.15)]'
                                  : isOwnMessage
                                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-tr-sm'
                                    : 'bg-white/10 text-white rounded-tl-sm'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                        </div>

                        {/* Delete button */}
                        {(isOwnMessage || isAdmin) && (
                          <button
                            onClick={() => deleteMessage(message.message_id)}
                            className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity ${
                              isOwnMessage ? '-left-8' : '-right-8'
                            }`}
                          >
                            <Trash2 className="w-4 h-4 text-red-400 hover:text-red-300" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-white/10 bg-white/5">
            <div className="flex gap-3">
              {/* Current user avatar */}
              <div className="flex-shrink-0">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                    {user?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
              </div>
              
              <div className="flex-1 flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 bg-white/5 border-white/10 text-white"
                  maxLength={1000}
                  disabled={sending}
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-white/40 mt-2 ml-13">
              {newMessage.length}/1000 characters
            </p>
          </form>
        </div>

        {/* Online indicator */}
        <div className="mt-4 text-center">
          <span className="inline-flex items-center gap-2 text-sm text-white/40">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
            Chat updates every 5 seconds
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommunityChat;
